import { type DisplayDocument, Document } from '@startuptools/business-ui/document/Document';
import {
  type GqlCompany,
  type GqlOptionsSharesRegistration,
  type GqlValidatorStatus,
  type GqlWarrantProgram,
  GqlWarrantProgramKind,
} from '../../../../graphql/base-types.graphql';
import { type ComponentProps, useState } from 'react';
import { AlertDialog } from '../../../../components/simple-dialogs/AlertDialog';
import { Trans } from '@lingui/react';
import { Button } from '@startuptools/ui/Button';
import { Stack } from '@startuptools/ui/Stack';
import { Typography } from '@startuptools/ui/Typography';
import {
  useCompanySuspenseQuery,
  useDocGenWarrantSharesRegistrationFormMutation,
  useOsrErrorsLazyQuery,
  useWarrantProgramSuspenseQuery,
} from '../../../../graphql/react-operations';
import { useCompanyId } from '@startuptools/common/react-wrapper';
import { hasValidatorErrors } from '../../utils';
import { apiClient } from '../../../../common/http';
import { isNil, omit } from 'lodash-es';
import { InnerStripe } from '../../../../components/stripe/Stripe';
import { ErrorDisplay } from '../../../../components/error-display/ErrorDisplay';
import {
  useOptionsSharesRegistrationFormSuspenseQuery,
  OptionsSharesRegistrationFormDocument,
} from './OptionsSharesRegistrationForm.graphql';
import { OptionsDocumentsDocument } from '../../../stock-options/common/components/main-steps/options-documents/OptionsDocuments.graphql';
import { GqlDocSystemTagKind, GqlDocumentSignatureMethod } from '../../../../graphql/base-types.graphql';
import { Divider } from '@startuptools/ui/Divider';
import { DocumentSignatures } from '../../../../components/document-signatures/DocumentSignatures';
import { useCompanySubscriptionSuspenseQuery } from '../../../../components/side-nav/CompanySubscription.graphql';
import { OptionsSharesRegistrationDocument } from '../../../stock-options/OptionsSharesRegistration.graphql';
import { useNavigate } from '../../../../components/Navigate';

const getDefaultFilename = (
  or: Pick<GqlOptionsSharesRegistration, 'description'>,
  wp: Pick<GqlWarrantProgram, 'warrantKind' | 'name'>,
  company: Pick<GqlCompany, 'name'>,
) => {
  if (wp.warrantKind === GqlWarrantProgramKind.Warrant) {
    return `Registreringsformulär aktieteckning teckningsoptioner - ${company.name} - ${or?.description}`;
  } else if (wp.warrantKind === GqlWarrantProgramKind.Wise) {
    return `Registreringsformulär aktieteckning teckningsoptioner - ${company.name} - ${wp?.name}`;
  }
};

export const OptionsSharesRegistrationForm = ({
  warrantProgramId,
  optionsSharesRegistration,
  navigateToDecisionStep,
}: {
  warrantProgramId: string;
  optionsSharesRegistration: Pick<GqlOptionsSharesRegistration, 'id' | 'description' | 'signatoryId'> & {
    latestRegistrationForm?: DisplayDocument | null;
  };
  navigateToDecisionStep: () => void;
}) => {
  const companyId = useCompanyId();
  const { navigate } = useNavigate();
  const [validationErrors, setValidationErrors] = useState<Record<string, GqlValidatorStatus> | undefined>(undefined);
  const [showStripeDialog, setShowStripeDialog] = useState(false);

  const [fetchOsrErrors] = useOsrErrorsLazyQuery({
    variables: { companyId, id: optionsSharesRegistration.id },
    fetchPolicy: 'no-cache',
  });

  const {
    data: { warrantProgram },
  } = useWarrantProgramSuspenseQuery({ variables: { id: warrantProgramId } });
  const {
    data: { company },
  } = useCompanySuspenseQuery({ variables: { id: companyId } });
  const { data: { companySubscription } = { companySubscription: null } } = useCompanySubscriptionSuspenseQuery({
    variables: { companyId },
  });

  // eslint-disable-next-line sut/loading-mutation
  const [doGenerate] = useDocGenWarrantSharesRegistrationFormMutation({
    refetchQueries: [
      OptionsSharesRegistrationFormDocument,
      OptionsDocumentsDocument,
      OptionsSharesRegistrationDocument,
    ],
    awaitRefetchQueries: true,
  });

  const defaultFilename = getDefaultFilename(optionsSharesRegistration, warrantProgram, company);

  const companyCanUseProFeatures = companySubscription?.canUseProFeatures ?? false;

  return (
    <>
      <Document
        allowDigitalSignature={companyCanUseProFeatures}
        canUseProFeatures={companyCanUseProFeatures}
        documentSystemTagKind={GqlDocSystemTagKind.WarrantRegistrationForm}
        document={optionsSharesRegistration?.latestRegistrationForm}
        defaultFileName={defaultFilename}
        generate={async signatureMethod => {
          if (isNil(warrantProgram.paidForAt)) {
            setShowStripeDialog(true);
            return;
          }
          return doGenerate({
            variables: {
              companyId,
              optionsSharesRegistrationId: optionsSharesRegistration.id,
              signatureMethod: signatureMethod ?? GqlDocumentSignatureMethod.Analogue,
            },
          });
        }}
        preview={() => apiClient.previewWarrantSharesRegistrationForm(companyId, optionsSharesRegistration.id)}
        validate={async () => {
          const { data: osrErrorData } = await fetchOsrErrors();
          if (!osrErrorData) {
            return { isValid: false, errors: undefined };
          }
          const osrErrors = omit(osrErrorData.optionsSharesRegistrationErrors, 'todos');
          if (hasValidatorErrors(osrErrors)) {
            setValidationErrors({ ...osrErrors } as Record<string, GqlValidatorStatus>);
            return { isValid: false, errors: undefined };
          }
          return { isValid: true, errors: undefined };
        }}
        signaturesSlot={
          optionsSharesRegistration.latestRegistrationForm?.signatureMethod === GqlDocumentSignatureMethod.Digital &&
          companyCanUseProFeatures && (
            <>
              <Divider />
              <DocumentSignatures
                documentId={optionsSharesRegistration.latestRegistrationForm.id}
                scriveStatus={optionsSharesRegistration.latestRegistrationForm.scriveStatus}
                zignedAgreementStatus={optionsSharesRegistration.latestRegistrationForm.zignedAgreementStatus}
                signAllowedAction={optionsSharesRegistration.latestRegistrationForm.signAllowedAction}
                isQualifiedEsign
              />
            </>
          )
        }
      />
      {validationErrors && (
        <AlertDialog
          title={<Trans id="Error was encountered" />}
          acceptButtonText={<Trans id="Close" />}
          onAccept={() => {
            setValidationErrors(undefined);
          }}
        >
          <Typography>
            <Trans id="There are one or multiple problems that needs resolving before generating the document" />
          </Typography>
          <Stack spacing={2}>
            {Object.values(validationErrors)
              .filter(e => e.hasError)
              .map(e => (
                <ErrorDisplay
                  error={e.error}
                  key={e.error!.en.short}
                  actionButton={
                    e.error!.en.short === 'Missing meeting' ? (
                      <Button color="warning" variant="soft" onClick={navigateToDecisionStep}>
                        <Trans id="Go to decision" />
                      </Button>
                    ) : undefined
                  }
                />
              ))}
          </Stack>
        </AlertDialog>
      )}
      {showStripeDialog && warrantProgram.meetingConcern?.meetingId && (
        <InnerStripe
          companyId={warrantProgram.companyId}
          meetingId={warrantProgram.meetingConcern?.meetingId}
          onCancel={() => {
            setShowStripeDialog(false);
          }}
          onPaid={() => {
            setShowStripeDialog(false);
            return doGenerate({
              variables: {
                companyId,
                optionsSharesRegistrationId: optionsSharesRegistration.id,
                signatureMethod:
                  optionsSharesRegistration.latestRegistrationForm?.signatureMethod ??
                  GqlDocumentSignatureMethod.Analogue,
              },
            });
          }}
          onBillingRedirect={() => navigate('/settings/billing')}
        />
      )}
    </>
  );
};

export const OptionsSharesRegistrationFormNgWrap = ({
  osrId,
  ...props
}: Omit<ComponentProps<typeof OptionsSharesRegistrationForm>, 'optionsSharesRegistration'> & { osrId: string }) => {
  const companyId = useCompanyId();
  const {
    data: { optionsSharesRegistration },
  } = useOptionsSharesRegistrationFormSuspenseQuery({ variables: { companyId, osrId } });
  return <OptionsSharesRegistrationForm optionsSharesRegistration={optionsSharesRegistration} {...props} />;
};
